import { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  LayoutDashboard, CalendarDays, Share2, HelpCircle, Music, Mic,
  LogOut, Menu, X, Shield, Settings,
} from 'lucide-react';
import { toast } from 'sonner';
import ax from '@/lib/apiClient';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const modules = [
  { icon: Share2, label: 'Socials', path: '/dashboard/socials' },
  { icon: HelpCircle, label: 'Trivia', path: '/dashboard/trivia' },
  { icon: Music, label: 'Bingo', path: '/dashboard/bingo' },
  { icon: Mic, label: 'Karaoke', path: '/dashboard/karaoke' },
];

// Same order for every role: Dashboard first, then Schedule, then the modules.
const getNavItems = () => [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
  { icon: CalendarDays, label: 'Schedule', path: '/dashboard/schedule' },
  ...modules,
];

const roleLabels = { master_admin: 'Master Admin', account_owner: 'Account Owner', host: 'Host' };

export default function Layout() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [changePwForm, setChangePwForm] = useState({ current: '', new_pw: '', confirm: '' });
  const [changePwError, setChangePwError] = useState('');
  const [changePwLoading, setChangePwLoading] = useState(false);

  const navItems = getNavItems();
  const mustChange = user?.must_change_password;

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setChangePwError('');
    if (changePwForm.new_pw !== changePwForm.confirm) { setChangePwError('Passwords do not match'); return; }
    if (changePwForm.new_pw.length < 10) { setChangePwError('Password must be at least 10 characters'); return; }
    setChangePwLoading(true);
    try {
      await ax.post(`${API}/auth/change-password`, {
        current_password: changePwForm.current,
        new_password: changePwForm.new_pw,
      });
      toast.success('Password changed');
      setChangePwForm({ current: '', new_pw: '', confirm: '' });
      await refreshUser();
    } catch (err) {
      setChangePwError(err.response?.data?.detail || 'Failed to change password');
    } finally { setChangePwLoading(false); }
  };

  const initial = user?.name?.charAt(0)?.toUpperCase() || 'U';

  return (
    <div className="min-h-screen bg-[#000e2a] flex" data-testid="app-shell">
      <button data-testid="sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-[#141b50] rounded-lg border border-[rgba(251,221,104,0.15)] text-[#fbdd68]">
        {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>
      {sidebarOpen && <div className="lg:hidden fixed inset-0 bg-black/60 z-30" onClick={() => setSidebarOpen(false)} />}

      <aside data-testid="app-sidebar" className={`fixed lg:sticky top-0 left-0 h-screen w-64 bg-[#0a1033] border-r border-[rgba(251,221,104,0.15)] flex flex-col z-40 transition-transform duration-200 shrink-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="p-5 border-b border-[rgba(251,221,104,0.15)] cursor-pointer group" onClick={() => navigate('/dashboard')} data-testid="header-logo">
          <div className="flex items-center gap-2.5">
            <img src="/hat-logo.png" alt="BIG Hat" className="w-9 h-9 object-contain drop-shadow-[0_0_10px_rgba(251,221,104,0.35)] group-hover:drop-shadow-[0_0_16px_rgba(251,221,104,0.55)] transition-all duration-300" />
            <div>
              <h1 className="text-xl font-bold text-[#fbdd68] tracking-tight leading-none font-lemonada">BIG Hat</h1>
              <p className="text-[9px] font-mono uppercase tracking-[0.25em] text-[#8892b0] mt-0.5 flex items-center gap-1.5">
                Command Center
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#22c55e] shadow-[0_0_6px_rgba(34,197,94,0.8)]" />
              </p>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto flex flex-col">
          <p className="px-3 pb-2 text-[9px] font-mono uppercase tracking-[0.3em] text-[#8892b0]/60">Navigate</p>
          <div className="space-y-1 flex-1">
            {navItems.map((item) => {
              return (
                <button key={item.label} data-testid={`sidebar-${item.label.toLowerCase().replace(/\s/g, '-')}-link`}
                  onClick={() => { navigate(item.path); setSidebarOpen(false); }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 active:scale-[0.98]
                    ${location.pathname === item.path
                      ? 'bg-[#fbdd68]/10 text-[#fbdd68] shadow-[inset_2px_0_0_#fbdd68,inset_0_0_18px_rgba(251,221,104,0.08)]'
                      : 'text-[#8892b0] hover:text-white hover:bg-white/5 hover:translate-x-0.5'}`}>
                  <item.icon className="w-4 h-4" />{item.label}
                </button>
              );
            })}
          </div>
          {/* Settings pinned at bottom of nav */}
          <div className="pt-2 mt-auto">
            <button data-testid="sidebar-settings-link"
              onClick={() => { navigate('/dashboard/settings'); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 active:scale-[0.98]
                ${location.pathname === '/dashboard/settings'
                  ? 'bg-[#fbdd68]/10 text-[#fbdd68] shadow-[inset_2px_0_0_#fbdd68,inset_0_0_18px_rgba(251,221,104,0.08)]'
                  : 'text-[#8892b0] hover:text-white hover:bg-white/5 hover:translate-x-0.5'}`}>
              <Settings className="w-4 h-4" />Settings
            </button>
          </div>
        </nav>

        <div className="p-4 border-t border-[rgba(251,221,104,0.15)] bg-[#000a20]/40">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-[#fbdd68]/15 flex items-center justify-center ring-2 ring-[#fbdd68]/30 shadow-[0_0_12px_rgba(251,221,104,0.15)]">
              {user?.picture ? <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover" /> : <span className="text-xs font-bold text-[#fbdd68]">{initial}</span>}
            </div>
            <div className="flex-1 min-w-0">
              <p data-testid="sidebar-user-name" className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-[10px] text-[#8892b0] truncate" data-testid="user-role-badge">{roleLabels[user?.role] || 'Host'}</p>
            </div>
          </div>
          <Button data-testid="sidebar-logout-button" onClick={handleLogout} variant="ghost"
            className="w-full justify-start text-[#8892b0] hover:text-[#ef4444] hover:bg-[#ef4444]/10 text-sm">
            <LogOut className="w-4 h-4 mr-2" /> Sign Out
          </Button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <div key={location.pathname} className="p-4 sm:p-6 lg:p-8 pt-16 lg:pt-8 animate-fade-in-up"><Outlet /></div>
      </main>

      {/* Forced password change dialog */}
      <Dialog open={!!mustChange}>
        <DialogContent className="bg-[#141b50] border-[rgba(251,221,104,0.15)] text-white [&>button]:hidden">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#fbdd68]" /> Password Change Required
            </DialogTitle>
            <DialogDescription className="text-[#8892b0]">
              You must set a new password before continuing.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            {changePwError && <p data-testid="change-password-error" className="text-[#ef4444] text-sm">{changePwError}</p>}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-[#8892b0]">Current Password</Label>
              <Input data-testid="change-password-current-input" type="password" value={changePwForm.current}
                onChange={(e) => setChangePwForm(p => ({ ...p, current: e.target.value }))}
                className="bg-[#000e2a]/80 text-white border-[rgba(251,221,104,0.2)] focus:border-[#fbdd68]" required />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-[#8892b0]">New Password</Label>
              <Input data-testid="change-password-new-input" type="password" value={changePwForm.new_pw}
                onChange={(e) => setChangePwForm(p => ({ ...p, new_pw: e.target.value }))}
                className="bg-[#000e2a]/80 text-white border-[rgba(251,221,104,0.2)] focus:border-[#fbdd68]" required />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-[#8892b0]">Confirm New Password</Label>
              <Input data-testid="change-password-confirm-input" type="password" value={changePwForm.confirm}
                onChange={(e) => setChangePwForm(p => ({ ...p, confirm: e.target.value }))}
                className="bg-[#000e2a]/80 text-white border-[rgba(251,221,104,0.2)] focus:border-[#fbdd68]" required />
            </div>
            <Button data-testid="change-password-submit-button" type="submit" disabled={changePwLoading}
              className="w-full bg-[#fbdd68] text-[#000e2a] font-bold uppercase tracking-wider hover:bg-[#ffe685]">
              {changePwLoading ? 'Changing...' : 'Change Password'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
